import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import {
    HeadingText50,
    BodyTextMedium,
    Button,
    ButtonGroup,
    Form,
    FormInputSelect,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { mediaQueries } from 'src/shared/styles/variables';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import segment from 'src/public/img/segment.svg';
import { config } from 'src/config/config';

import { ChooseYourSegmentPageTestID } from '../models/testsIds';
import { MemberProfileInput } from '@modernhealth/circles-graphql/v2';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const Image = styled.img({
    width: '40%',
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(22),
    marginBottom: pxToRem(40),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
});
interface FormData {
    companySegmentUuid: string;
}

export const ChooseYourSegmentPage: React.FC = () => {
    const { t } = useTranslation();
    const { company, isDependent, companySegments, companySegment } =
        useSelector(getUserInformation);

    const dispatch = useDispatch<Dispatch>();

    if (companySegments?.length === 0) {
        navigationService.navigate('/all-your-benefits');
    }

    const formSchema = Yup.object().shape({
        companySegmentUuid: Yup.string()
            .trim()
            .required(t('benefits:choose_your_segment.employment_type_required')),
    });

    const formDefinition = useForm<FormData>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            companySegmentUuid: companySegment?.uuid,
        },
    });

    const handleBack = () => {
        navigationService.navigate('/');
    };

    const handleSubmit = async (data: FormData) => {
        try {
            await dispatch.user.updateMemberProfile(data as MemberProfileInput);
            toastNotificationsService.renderSuccessToast(t(config.messages.userInfoUpdateSuccess));
            navigationService.navigate('/all-your-benefits');
        } catch {
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
        }
    };

    const description = !isDependent
        ? t('benefits:choose_your_segment.employer_description')
        : t('benefits:choose_your_segment.dependent_description');

    return (
        <Wrapper data-testid={ChooseYourSegmentPageTestID.Container}>
            <Image src={segment} alt={description} />

            <TitleContainer>
                <HeadingText50>
                    {t('benefits:choose_your_segment.title', { company })}
                </HeadingText50>
                <BodyTextMedium>{description}</BodyTextMedium>
            </TitleContainer>

            <Form onSubmit={handleSubmit} definition={formDefinition}>
                <FormInputSelect
                    name="companySegmentUuid"
                    label={t('benefits:choose_your_segment.employment_type')}
                    options={companySegments?.map(c => ({ value: c.uuid, label: c.name })) ?? []}
                    data-testid={ChooseYourSegmentPageTestID.SegmentInput}
                    required
                />
                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ChooseYourSegmentPageTestID.BackHome}
                        >
                            {t('common.back_to_home')}
                        </Button>
                        <Button
                            busy={formDefinition.formState.isSubmitting}
                            variant="primary"
                            size="large"
                            data-testid={ChooseYourSegmentPageTestID.Continue}
                            type="submit"
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </Form>
        </Wrapper>
    );
};
